import { useCallback, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'

import {
  SMART_ACCOUNTS_SETUP_STEPS,
  SMART_ACCOUNTS_SETUP_STEPS_ORDER,
} from '~/constants/orgs'
import { QUERY_KEYS } from '~/constants/react-query'
import { getActiveOrgQueryKey, useRequiredActiveOrg } from '~/hooks/org'
import { useRequiredAuthUser } from '~/hooks/useAuthUser'
import { getPostOnboardingChecklistQueryKey } from '~/hooks/usePostOnboardingChecklist'
import { useServerRequests } from '~/hooks/useRequests'
import { AuthOrg, AuthUser, SmartAccountsSetupStep } from '~/types'

export type StepIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const getUserTreasuryPasskeyId = (userId: string, org: AuthOrg) => {
  return org.passkeys
    .filter((p) => p.user?.id === userId)
    .find((p) =>
      org.smartAccountSetup?.authenticators?.find(
        (a) => a.authenticatorId === p.id && a.setupAccount === 'TREASURY',
      ),
    )
}

const hasCompletedProfile = (user: AuthUser, org: AuthOrg) => {
  return !!(
    user.displayName &&
    user.profilePhotoUrl &&
    org.passkeys.some((p) => p.user?.id === user.id) &&
    getUserTreasuryPasskeyId(user.id, org)
  )
}

const hasCompletedWorkspaceInfo = (currentStep?: SmartAccountsSetupStep) => {
  return currentStep && currentStep !== 'TEAM'
}

const calculateAvailableStep = (
  user: AuthUser,
  org: AuthOrg,
  setup: AuthOrg['smartAccountSetup'],
) => {
  // If workspace info is not complete, can only access workspace step (0)
  if (!hasCompletedWorkspaceInfo(setup?.currentStep)) {
    return 0 as StepIndex
  }
  // If user info is not complete (including treasury passkey), can access workspace and user steps (0-1)
  if (!hasCompletedProfile(user, org)) {
    return 1 as StepIndex
  }
  // Otherwise, the latest step from setup is available
  return SMART_ACCOUNTS_SETUP_STEPS_ORDER.indexOf(
    setup?.currentStep ?? 'TEAM',
  ) as StepIndex
}

const calculateInitialStep = (
  user: AuthUser,
  org: AuthOrg,
  setup?: AuthOrg['smartAccountSetup'],
) => {
  // The initial step is the same as the available step
  return calculateAvailableStep(user, org, setup)
}

const calculateNextStep = (currentStep: StepIndex) => {
  if (currentStep === 5) {
    return 6 as StepIndex
  }
  return Math.min(5, currentStep + 1) as StepIndex
}

const getStepKey = (stepIndex: StepIndex) => {
  return Object.keys(SMART_ACCOUNTS_SETUP_STEPS)[
    stepIndex
  ] as SmartAccountsSetupStep
}

export function useSmartAccountSetupStep() {
  const org = useRequiredActiveOrg()
  const user = useRequiredAuthUser()
  const setup = org.smartAccountSetup
  const { serverPostRequest } = useServerRequests()
  const { push } = useRouter()
  const queryClient = useQueryClient()
  const availableStep = calculateAvailableStep(user, org, setup)
  const initialStep = calculateInitialStep(user, org, setup)

  const [step, setStep] = useState<StepIndex>(initialStep)
  const [isSavingFinalStep, setIsSavingFinalStep] = useState(false)

  const stepForward = useCallback(async () => {
    const startingStepKey = getStepKey(step)
    const nextStep = calculateNextStep(step)

    // On the final step we want to show a saving spinner state and wait
    // for a successful API response before redirecting to '/'
    if (nextStep === 6) {
      setIsSavingFinalStep(true)
    } else {
      // Update UI optimistically
      setStep(nextStep)
    }

    // Send API request to update backend
    const res = await serverPostRequest<{ treasuryAddress?: string }>({
      path: '/api/org/smart_accounts_setup/step_forward',
      params: { orgId: org.id, startingStep: startingStepKey },
      errorMessage: 'Failed to step forward',
      onError: () => {
        // Rollback the optimistic update
        setStep(step)
        setIsSavingFinalStep(false)
      },
    })

    if (res) {
      // Invalidate org query to update availableStep for breadcrumb navigation
      await queryClient.invalidateQueries({
        queryKey: getActiveOrgQueryKey(org.id),
      })

      // Redirect to '/' if setup is complete
      if (res.data?.treasuryAddress) {
        // The backend creates the checklist with the treasury.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getPostOnboardingChecklistQueryKey(org.id),
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.AUTH_USER],
          }),
        ])
        push('/')
      }
    }
  }, [step, org.id, serverPostRequest, push, queryClient])

  const stepBack = useCallback(() => {
    setStep((prevStep) => Math.max(0, prevStep - 1) as StepIndex)
  }, [])

  return {
    step,
    setStep,
    stepForward,
    stepBack,
    availableStep,
    isSavingFinalStep,
  }
}
